/**
 * AngajatiTab - Componenta pentru tab-ul Angajați
 * Extrasă din AdminDashboard.jsx pentru modularizare și performanță
 */
import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { 
  Plus, Edit, Trash2, Calendar, Users, UserCheck, UserX, 
  CalendarX, ChevronDown, Lock, LockOpen, Key, RefreshCw 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const TimeSelect = memo(function TimeSelect({ value, onChange, placeholder }) {
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];
  
  const [hour, minute] = (value || '08:00').split(':');
  
  return (
    <div className="flex gap-1">
      <Select value={hour} onValueChange={(h) => onChange(`${h}:${minute}`)}>
        <SelectTrigger className="w-16 h-8 text-xs">
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent>
          {hours.map(h => (
            <SelectItem key={h} value={h}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-slate-400 self-center">:</span>
      <Select value={minute} onValueChange={(m) => onChange(`${hour}:${m}`)}>
        <SelectTrigger className="w-16 h-8 text-xs">
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent>
          {minutes.map(m => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
});

const AngajatiTab = memo(function AngajatiTab({
  drivers,
  newDriver,
  setNewDriver,
  editingDriver,
  setEditingDriver,
  openDriverDialog,
  setOpenDriverDialog,
  openEditDriverDialog,
  setOpenEditDriverDialog,
  openResetPasswordDialog,
  setOpenResetPasswordDialog,
  resetPasswordUser,
  resetPasswordData,
  setResetPasswordData,
  employeeStatusFilter,
  setEmployeeStatusFilter,
  employeeSearchFilter,
  setEmployeeSearchFilter,
  pontajEndDateDialog,
  setPontajEndDateDialog,
  handleCreateDriver,
  handleEditDriver,
  handleUpdateDriver,
  handleDeleteDriver,
  handleOpenResetPassword,
  handleResetPassword,
  handleToggleAcces,
  fetchDrivers,
  fetchPontajData,
  getDisplayName,
  getAuthHeaders,
  hasPermission,
  API
}) {
  const [reactivareDialog, setReactivareDialog] = useState({ open: false, driver: null });
  const [dataReangajare, setDataReangajare] = useState('');

  const handleReactivare = async () => {
    if (!dataReangajare || !reactivareDialog.driver) return;
    try {
      await axios.post(
        `${API}/pontaj/reactivare?employee_id=${reactivareDialog.driver.id}&data_reangajare=${dataReangajare}`,
        {},
        getAuthHeaders()
      );
      fetchDrivers();
      if (fetchPontajData) fetchPontajData();
      toast.success(`Angajatul ${reactivareDialog.driver.name || reactivareDialog.driver.username} a fost reactivat.`);
      setReactivareDialog({ open: false, driver: null });
      setDataReangajare('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la reactivare');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center gap-4">
            <div>
              <CardTitle>Angajați</CardTitle>
              <CardDescription>Administrează conturile și rolurile angajaților</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {/* Status Filter */}
              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setEmployeeStatusFilter('active')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    employeeStatusFilter === 'active' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Activi
                </button>
                <button
                  onClick={() => setEmployeeStatusFilter('former')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    employeeStatusFilter === 'former' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Foști
                </button>
                <button
                  onClick={() => setEmployeeStatusFilter('all')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    employeeStatusFilter === 'all' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Toți
                </button>
              </div>
              <Input
                placeholder="Caută după nume..."
                value={employeeSearchFilter}
                onChange={(e) => setEmployeeSearchFilter(e.target.value)}
                className="w-64"
              />
            </div>
            {hasPermission('angajati.creare') && (
              <Dialog open={openDriverDialog} onOpenChange={setOpenDriverDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-employee-button" className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Adaugă Angajat
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Adaugă Angajat Nou</DialogTitle>
                    <DialogDescription>Create a new employee account</DialogDescription>
                  </DialogHeader>
                  <form autoComplete="off" className="space-y-4 pt-4 max-h-[600px] overflow-y-auto">
                    <div>
                      <Label htmlFor="driver-username">Utilizator</Label>
                      <Input
                        id="driver-username"
                        data-testid="driver-username-input"
                        placeholder="Enter username"
                        autoComplete="off"
                        value={newDriver.username}
                        onChange={(e) => setNewDriver({ ...newDriver, username: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="driver-password">Parolă</Label>
                      <Input
                        id="driver-password"
                        data-testid="driver-password-input"
                        type="password"
                        autoComplete="new-password"
                        placeholder="Enter password"
                        value={newDriver.password}
                        onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="driver-nume">Nume</Label>
                        <Input
                          id="driver-nume"
                          data-testid="driver-nume-input"
                          placeholder="ex: Cumpată"
                          value={newDriver.nume}
                          onChange={(e) => setNewDriver({ ...newDriver, nume: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="driver-prenume">Prenume</Label>
                        <Input
                          id="driver-prenume"
                          data-testid="driver-prenume-input"
                          placeholder="ex: Adrian"
                          value={newDriver.prenume}
                          onChange={(e) => setNewDriver({ ...newDriver, prenume: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="driver-cnp">CNP</Label>
                      <Input
                        id="driver-cnp"
                        data-testid="driver-cnp-input"
                        placeholder="ex: 1234567890123"
                        value={newDriver.cnp}
                        onChange={(e) => setNewDriver({ ...newDriver, cnp: e.target.value })}
                      />
                    </div>
                    <p className="text-xs text-slate-500 -mt-2">Numele real și CNP care vor apărea în documente oficiale (Pontaj, Evidență ore)</p>
                    <div>
                      <Label htmlFor="driver-functie">Funcție</Label>
                      <Input
                        id="driver-functie"
                        placeholder="ex: Șofer, Contabil, Jurist..."
                        value={newDriver.functie}
                        onChange={(e) => setNewDriver({ ...newDriver, functie: e.target.value })}
                      />
                      <p className="text-xs text-slate-500 mt-1">Funcția în cadrul societății</p>
                    </div>
                    <div>
                      <Label htmlFor="employee-role">Rol Principal</Label>
                      <Select 
                        value={newDriver.role} 
                        onValueChange={(value) => {
                          const currentRoles = newDriver.roles || [newDriver.role];
                          const updatedRoles = currentRoles.includes(value) ? currentRoles : [value, ...currentRoles.filter(r => r !== newDriver.role)];
                          setNewDriver({ ...newDriver, role: value, roles: updatedRoles });
                        }}
                      >
                        <SelectTrigger data-testid="employee-role-select">
                          <SelectValue placeholder="Selectează rol principal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="driver">Șofer</SelectItem>
                          <SelectItem value="tesa">TESA</SelectItem>
                          <SelectItem value="dispatcher">Dispecer</SelectItem>
                          <SelectItem value="fleet_manager">Manager Flotă</SelectItem>
                          <SelectItem value="contabilitate">Contabilitate</SelectItem>
                          <SelectItem value="juridic">Juridic</SelectItem>
                          <SelectItem value="admin">Administrator</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Roluri Adiționale (opțional)</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-md bg-slate-50">
                        {[
                          { value: 'contabilitate', label: 'Contabilitate' },
                          { value: 'juridic', label: 'Juridic' },
                          { value: 'fleet_manager', label: 'Manager Flotă' },
                          { value: 'dispatcher', label: 'Dispecer' },
                          { value: 'tesa', label: 'TESA' }
                        ].filter(r => r.value !== newDriver.role).map(roleOption => (
                          <label key={roleOption.value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                            <input
                              type="checkbox"
                              checked={(newDriver.roles || []).includes(roleOption.value)}
                              onChange={(e) => {
                                const currentRoles = newDriver.roles || [newDriver.role];
                                let updatedRoles;
                                if (e.target.checked) {
                                  updatedRoles = [...currentRoles, roleOption.value];
                                } else {
                                  updatedRoles = currentRoles.filter(r => r !== roleOption.value);
                                }
                                if (!updatedRoles.includes(newDriver.role)) {
                                  updatedRoles = [newDriver.role, ...updatedRoles];
                                }
                                setNewDriver({ ...newDriver, roles: updatedRoles });
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm">{roleOption.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Bifează pentru a permite utilizatorului să schimbe între roluri</p>
                    </div>
                    <div>
                      <Label htmlFor="id-expiry">ID Expiry Date</Label>
                      <Input
                        id="id-expiry"
                        data-testid="id-expiry-input"
                        type="date"
                        value={newDriver.id_expiry || ''}
                        onChange={(e) => setNewDriver({ ...newDriver, id_expiry: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="driver-licence-expiry">Driver Licence Expiry Date</Label>
                      <Input
                        id="driver-licence-expiry"
                        data-testid="driver-licence-expiry-input"
                        type="date"
                        value={newDriver.driver_licence_expiry || ''}
                        onChange={(e) => setNewDriver({ ...newDriver, driver_licence_expiry: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="work-medicine-expiry">Work Medicine Expiry Date</Label>
                      <Input
                        id="work-medicine-expiry"
                        data-testid="work-medicine-expiry-input"
                        type="date"
                        value={newDriver.work_medicine_expiry || ''}
                        onChange={(e) => setNewDriver({ ...newDriver, work_medicine_expiry: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="catg">Catg</Label>
                      <Input
                        id="catg"
                        data-testid="catg-input"
                        placeholder="Enter category (e.g., A, B, C)"
                        value={newDriver.catg}
                        onChange={(e) => setNewDriver({ ...newDriver, catg: e.target.value.toUpperCase() })}
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="numar_contract">Nr. Contract</Label>
                        <Input
                          id="numar_contract"
                          type="text"
                          placeholder="ex: 1, 2, 3..."
                          value={newDriver.numar_contract || ''}
                          onChange={(e) => setNewDriver({ ...newDriver, numar_contract: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="angajat_de_la">Data angajării</Label>
                        <Input
                          id="angajat_de_la"
                          type="date"
                          value={newDriver.angajat_de_la || ''}
                          onChange={(e) => setNewDriver({ ...newDriver, angajat_de_la: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 -mt-2">Nr. contract determină ordinea în pontaj. Zilele anterioare datei angajării vor fi marcate cu X.</p>
                    
                    {/* Normă și Program de Lucru */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-medium text-sm mb-3 text-slate-700">Normă și Program de Lucru</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Normă zilnică</Label>
                            <div className="flex gap-2 mt-1">
                              {[2, 4, 6, 8].map((ore) => (
                                <Button
                                  key={ore}
                                  type="button"
                                  variant={newDriver.norma_ore === ore ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setNewDriver({ ...newDriver, norma_ore: ore })}
                                  className="flex-1"
                                >
                                  {ore}h
                                </Button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Zile CO anuale (contract)</Label>
                            <Input
                              type="number"
                              min="0"
                              max="50"
                              value={newDriver.zile_co_anuale || 21}
                              onChange={(e) => setNewDriver({ ...newDriver, zile_co_anuale: parseInt(e.target.value) || 21 })}
                              className="mt-1"
                              placeholder="21"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Program start</Label>
                            <div className="mt-1">
                              <TimeSelect
                                value={newDriver.program_start || '08:00'}
                                onChange={(val) => setNewDriver({ ...newDriver, program_start: val })}
                                placeholder="Start"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Program sfârșit</Label>
                            <div className="mt-1">
                              <TimeSelect
                                value={newDriver.program_sfarsit || '17:00'}
                                onChange={(val) => setNewDriver({ ...newDriver, program_sfarsit: val })}
                                placeholder="Sfârșit"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newDriver.program_pauza !== false}
                            onChange={(e) => setNewDriver({ ...newDriver, program_pauza: e.target.checked })}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <span className="text-sm text-slate-600">Include pauză de masă</span>
                        </label>
                        
                        {newDriver.program_pauza !== false && (
                          <div className="grid grid-cols-2 gap-3 pl-6">
                            <div>
                              <Label className="text-xs">Pauză de la</Label>
                              <div className="mt-1">
                                <TimeSelect
                                  value={newDriver.pauza_start || '12:00'}
                                  onChange={(val) => setNewDriver({ ...newDriver, pauza_start: val })}
                                  placeholder="De la"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Pauză până la</Label>
                              <div className="mt-1">
                                <TimeSelect
                                  value={newDriver.pauza_sfarsit || '13:00'}
                                  onChange={(val) => setNewDriver({ ...newDriver, pauza_sfarsit: val })}
                                  placeholder="Până la"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button onClick={handleCreateDriver} data-testid="submit-driver-button" className="w-full">
                      Adaugă Angajat
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Edit Driver Dialog */}
            {hasPermission('angajati.editare') && (
              <Dialog open={openEditDriverDialog} onOpenChange={setOpenEditDriverDialog}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Editează Angajat</DialogTitle>
                    <DialogDescription>Update driver details and expiry dates</DialogDescription>
                  </DialogHeader>
                  {editingDriver && (
                    <div className="space-y-4 pt-4 max-h-[600px] overflow-y-auto">
                      <div>
                        <Label htmlFor="edit-driver-username">Utilizator</Label>
                        <Input
                          id="edit-driver-username"
                          data-testid="edit-driver-username-input"
                          value={editingDriver.username}
                          onChange={(e) => setEditingDriver({ ...editingDriver, username: e.target.value })}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-driver-nume">Nume</Label>
                          <Input
                            id="edit-driver-nume"
                            data-testid="edit-driver-nume-input"
                            placeholder="ex: Cumpată"
                            value={editingDriver.nume || ''}
                            onChange={(e) => setEditingDriver({ ...editingDriver, nume: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-driver-prenume">Prenume</Label>
                          <Input
                            id="edit-driver-prenume"
                            data-testid="edit-driver-prenume-input"
                            placeholder="ex: Adrian"
                            value={editingDriver.prenume || ''}
                            onChange={(e) => setEditingDriver({ ...editingDriver, prenume: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="edit-driver-cnp">CNP</Label>
                        <Input
                          id="edit-driver-cnp"
                          data-testid="edit-driver-cnp-input"
                          placeholder="ex: 1234567890123"
                          value={editingDriver.cnp || ''}
                          onChange={(e) => setEditingDriver({ ...editingDriver, cnp: e.target.value })}
                        />
                      </div>
                      <p className="text-xs text-slate-500 -mt-2">Numele real și CNP pentru documente oficiale</p>

                      <div>
                        <Label htmlFor="edit-driver-cont-bancar">Cont Bancar (IBAN)</Label>
                        <Input
                          id="edit-driver-cont-bancar"
                          data-testid="edit-driver-cont-bancar-input"
                          placeholder="ex: RO49AAAA1B31007593840000"
                          value={editingDriver.cont_bancar || ''}
                          onChange={(e) => setEditingDriver({ ...editingDriver, cont_bancar: e.target.value.toUpperCase() })}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-driver-email">📧 Email (pentru 2FA)</Label>
                        <Input
                          id="edit-driver-email"
                          data-testid="edit-driver-email-input"
                          type="email"
                          placeholder="ex: admin@crimacotrp.ro"
                          value={editingDriver.email || ''}
                          onChange={(e) => setEditingDriver({ ...editingDriver, email: e.target.value })}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {(editingDriver.roles || []).includes('admin') 
                            ? '⚠️ Obligatoriu pentru autentificare în 2 pași (2FA)'
                            : 'Opțional - pentru notificări email'}
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="edit-driver-functie">Funcție</Label>
                        <Input
                          id="edit-driver-functie"
                          placeholder="ex: Șofer, Contabil, Jurist..."
                          value={editingDriver.functie || ''}
                          onChange={(e) => setEditingDriver({ ...editingDriver, functie: e.target.value })}
                        />
                        <p className="text-xs text-slate-500 mt-1">Funcția în cadrul societății</p>
                      </div>

                      <div>
                        <Label htmlFor="edit-driver-role">Rol Principal</Label>
                        <Select 
                          value={editingDriver.role} 
                          onValueChange={(value) => {
                            const currentRoles = editingDriver.roles || [editingDriver.role];
                            const updatedRoles = currentRoles.includes(value) ? currentRoles : [value, ...currentRoles.filter(r => r !== editingDriver.role)];
                            setEditingDriver({ ...editingDriver, role: value, roles: updatedRoles });
                          }}
                        >
                          <SelectTrigger id="edit-driver-role">
                            <SelectValue placeholder="Selectează rol principal" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="driver">Șofer</SelectItem>
                            <SelectItem value="tesa">TESA</SelectItem>
                            <SelectItem value="dispatcher">Dispecer</SelectItem>
                            <SelectItem value="fleet_manager">Manager Flotă</SelectItem>
                            <SelectItem value="contabilitate">Contabilitate</SelectItem>
                            <SelectItem value="juridic">Juridic</SelectItem>
                            <SelectItem value="admin">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Roluri Adiționale</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-md bg-slate-50">
                          {[
                            { value: 'contabilitate', label: 'Contabilitate' },
                            { value: 'juridic', label: 'Juridic' },
                            { value: 'fleet_manager', label: 'Manager Flotă' },
                            { value: 'dispatcher', label: 'Dispecer' },
                            { value: 'tesa', label: 'TESA' },
                            { value: 'admin', label: 'Administrator' }
                          ].filter(r => r.value !== editingDriver.role).map(roleOption => (
                            <label key={roleOption.value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                              <input
                                type="checkbox"
                                checked={(editingDriver.roles || []).includes(roleOption.value)}
                                onChange={(e) => {
                                  const currentRoles = editingDriver.roles || [editingDriver.role];
                                  let updatedRoles;
                                  if (e.target.checked) {
                                    updatedRoles = [...currentRoles, roleOption.value];
                                  } else {
                                    updatedRoles = currentRoles.filter(r => r !== roleOption.value);
                                  }
                                  if (!updatedRoles.includes(editingDriver.role)) {
                                    updatedRoles = [editingDriver.role, ...updatedRoles];
                                  }
                                  setEditingDriver({ ...editingDriver, roles: updatedRoles });
                                }}
                                className="rounded border-slate-300"
                              />
                              <span className="text-sm">{roleOption.label}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Roluri actuale: {(editingDriver.roles || [editingDriver.role]).map(r => 
                            r === 'admin' ? 'Admin' : r === 'fleet_manager' ? 'Manager Flotă' : r === 'contabilitate' ? 'Contabilitate' : r === 'juridic' ? 'Juridic' : r === 'dispatcher' ? 'Dispecer' : r === 'tesa' ? 'TESA' : r === 'driver' ? 'Șofer' : r
                          ).join(', ')}
                        </p>
                      </div>
                      
                      <div>
                        <Label>ID Expiry Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="w-full justify-start text-left" 
                              data-testid="edit-id-expiry-picker"
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              {editingDriver.id_expiry ? format(editingDriver.id_expiry, 'PPP') : 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent 
                              mode="single" 
                              selected={editingDriver.id_expiry} 
                              onSelect={(date) => setEditingDriver({ ...editingDriver, id_expiry: date })} 
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      <div>
                        <Label>Driver Licence Expiry Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="w-full justify-start text-left" 
                              data-testid="edit-driver-licence-expiry-picker"
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              {editingDriver.driver_licence_expiry ? format(editingDriver.driver_licence_expiry, 'PPP') : 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent 
                              mode="single" 
                              selected={editingDriver.driver_licence_expiry} 
                              onSelect={(date) => setEditingDriver({ ...editingDriver, driver_licence_expiry: date })} 
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      <div>
                        <Label>Work Medicine Expiry Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button 
                              variant="outline" 
                              className="w-full justify-start text-left" 
                              data-testid="edit-work-medicine-expiry-picker"
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              {editingDriver.work_medicine_expiry ? format(editingDriver.work_medicine_expiry, 'PPP') : 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent 
                              mode="single" 
                              selected={editingDriver.work_medicine_expiry} 
                              onSelect={(date) => setEditingDriver({ ...editingDriver, work_medicine_expiry: date })} 
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-catg">Catg</Label>
                        <Input
                          id="edit-catg"
                          data-testid="edit-catg-input"
                          placeholder="Enter category"
                          value={editingDriver.catg || ''}
                          onChange={(e) => setEditingDriver({ ...editingDriver, catg: e.target.value.toUpperCase() })}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="edit-numar_contract">Nr. Contract</Label>
                          <Input
                            id="edit-numar_contract"
                            type="text"
                            placeholder="ex: 1, 2, 3..."
                            value={editingDriver.numar_contract || ''}
                            onChange={(e) => setEditingDriver({ ...editingDriver, numar_contract: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-angajat_de_la">Data angajării</Label>
                          <Input
                            id="edit-angajat_de_la"
                            type="date"
                            value={editingDriver.angajat_de_la || ''}
                            onChange={(e) => setEditingDriver({ ...editingDriver, angajat_de_la: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 -mt-2">Nr. contract determină ordinea în pontaj</p>
                      
                      {/* Normă și Program Standard */}
                      <div className="border-t pt-4 mt-4">
                        <h4 className="font-medium text-sm mb-3 text-slate-700">Normă și Program de Lucru</h4>
                        
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Normă zilnică</Label>
                              <div className="flex gap-2 mt-1">
                                {[2, 4, 6, 8].map(ore => (
                                  <button
                                    key={ore}
                                    type="button"
                                    onClick={() => setEditingDriver({ ...editingDriver, norma_ore: ore })}
                                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                                      editingDriver.norma_ore === ore
                                        ? 'border-green-500 bg-green-100 text-green-700'
                                        : 'border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600'
                                    }`}
                                  >
                                    {ore}h
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Zile CO anuale (contract)</Label>
                              <Input
                                type="number"
                                min="0"
                                max="50"
                                value={editingDriver.zile_co_anuale || 21}
                                onChange={(e) => setEditingDriver({ ...editingDriver, zile_co_anuale: parseInt(e.target.value) || 21 })}
                                className="mt-1"
                                placeholder="21"
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Program start</Label>
                              <div className="mt-1">
                                <TimeSelect
                                  value={editingDriver.program_start || '08:00'}
                                  onChange={(val) => setEditingDriver({ ...editingDriver, program_start: val })}
                                  placeholder="Start"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Program sfârșit</Label>
                              <div className="mt-1">
                                <TimeSelect
                                  value={editingDriver.program_sfarsit || '17:00'}
                                  onChange={(val) => setEditingDriver({ ...editingDriver, program_sfarsit: val })}
                                  placeholder="Sfârșit"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingDriver.program_pauza !== false}
                              onChange={(e) => setEditingDriver({ ...editingDriver, program_pauza: e.target.checked })}
                              className="w-4 h-4 rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-600">Include pauză de masă</span>
                          </label>
                          
                          {editingDriver.program_pauza !== false && (
                            <div className="grid grid-cols-2 gap-3 mt-2 pl-6">
                              <div>
                                <Label className="text-xs">Pauză de la</Label>
                                <div className="mt-1">
                                  <TimeSelect
                                    value={editingDriver.pauza_start || '12:00'}
                                    onChange={(val) => setEditingDriver({ ...editingDriver, pauza_start: val })}
                                    placeholder="De la"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Pauză până la</Label>
                                <div className="mt-1">
                                  <TimeSelect
                                    value={editingDriver.pauza_sfarsit || '13:00'}
                                    onChange={(val) => setEditingDriver({ ...editingDriver, pauza_sfarsit: val })}
                                    placeholder="Până la"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <Button onClick={handleUpdateDriver} data-testid="update-driver-button" className="w-full">
                        Actualizează Angajat
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            )}
            
            {/* Reset Password Dialog */}
            <Dialog open={openResetPasswordDialog} onOpenChange={setOpenResetPasswordDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Resetare Parolă</DialogTitle>
                  <DialogDescription>
                    Resetează parola pentru {resetPasswordUser?.username}
                  </DialogDescription>
                </DialogHeader>
                {resetPasswordUser && (
                  <form autoComplete="off" className="space-y-4 pt-4">
                    <input
                      type="text"
                      name="username"
                      autoComplete="username"
                      value={resetPasswordUser.username}
                      readOnly
                      style={{ position: 'absolute', left: '-9999px' }}
                    />
                    <div>
                      <Label htmlFor="new-password">Parolă Nouă</Label>
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="Introdu parola nouă"
                        value={resetPasswordData.password}
                        onChange={(e) => setResetPasswordData({ ...resetPasswordData, password: e.target.value })}
                        onFocus={(e) => e.target.removeAttribute('readonly')}
                        data-testid="new-password-input"
                        autoComplete="off"
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password">Confirmă Parola</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Confirmă parola nouă"
                        value={resetPasswordData.confirmPassword}
                        onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                        onFocus={(e) => e.target.removeAttribute('readonly')}
                        data-testid="confirm-password-input"
                        autoComplete="off"
                        readOnly
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setOpenResetPasswordDialog(false)} 
                        className="flex-1"
                        type="button"
                      >
                        Anulează
                      </Button>
                      <Button 
                        onClick={handleResetPassword} 
                        className="flex-1"
                        data-testid="confirm-reset-password-button"
                        type="button"
                      >
                        Resetează Parola
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="py-2 px-2">Nr.</TableHead>
                  <TableHead className="py-2 px-2">Angajat</TableHead>
                  <TableHead className="py-2 px-2">Utilizator</TableHead>
                  <TableHead className="py-2 px-2">Nume</TableHead>
                  <TableHead className="py-2 px-2">Funcție</TableHead>
                  <TableHead className="py-2 px-2">CNP</TableHead>
                  <TableHead className="py-2 px-2">Rol</TableHead>
                  <TableHead className="py-2 px-2">Normă</TableHead>
                  <TableHead className="py-2 px-2">Program</TableHead>
                  <TableHead className="py-2 px-2">Pontaj</TableHead>
                  <TableHead className="py-2 px-2">ID</TableHead>
                  <TableHead className="py-2 px-2">Permis</TableHead>
                  <TableHead className="py-2 px-2">Med.</TableHead>
                  <TableHead className="py-2 px-2">Cat.</TableHead>
                  <TableHead className="py-2 px-2">Creat de</TableHead>
                  <TableHead className="py-2 px-2 text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...drivers]
                  .filter(driver => {
                    const isFormer = driver.fost_angajat === true || driver.pontaj_pana_la != null;
                    if (employeeStatusFilter === 'active' && isFormer) return false;
                    if (employeeStatusFilter === 'former' && !isFormer) return false;
                    
                    if (!employeeSearchFilter) return true;
                    const searchLower = employeeSearchFilter.toLowerCase();
                    const fullName = `${driver.nume || ''} ${driver.prenume || ''}`.toLowerCase();
                    const username = (driver.username || '').toLowerCase();
                    const functie = (driver.functie || '').toLowerCase();
                    return fullName.includes(searchLower) || username.includes(searchLower) || functie.includes(searchLower);
                  })
                  .sort((a, b) => {
                    const aExcluded = a.in_pontaj === false;
                    const bExcluded = b.in_pontaj === false;
                    if (aExcluded && !bExcluded) return 1;
                    if (!aExcluded && bExcluded) return -1;
                    if (aExcluded && bExcluded) {
                      const aNum = parseInt(a.numar_contract) || 999999;
                      const bNum = parseInt(b.numar_contract) || 999999;
                      return aNum - bNum;
                    }
                    const aHasContract = a.numar_contract && a.numar_contract.trim() !== '';
                    const bHasContract = b.numar_contract && b.numar_contract.trim() !== '';
                    if (aHasContract && !bHasContract) return -1;
                    if (!aHasContract && bHasContract) return 1;
                    if (!aHasContract && !bHasContract) return 0;
                    const aNum = parseInt(a.numar_contract) || 999999;
                    const bNum = parseInt(b.numar_contract) || 999999;
                    return aNum - bNum;
                  }).map((driver) => (
                    <TableRow key={driver.id} data-testid={`driver-row-${driver.id}`} className={`${driver.in_pontaj === false ? 'bg-slate-50 opacity-60' : ''} hover:bg-slate-50`}>
                      <TableCell className="py-1.5 px-2 font-medium text-center text-xs">
                        {driver.numar_contract || '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2 text-xs text-gray-600">
                        {driver.angajat_de_la ? format(new Date(driver.angajat_de_la), 'dd.MM.yyyy') : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2 font-medium text-xs">{driver.username}</TableCell>
                      <TableCell className="py-1.5 px-2 text-xs text-gray-600">
                        {getDisplayName(driver) !== driver.username ? getDisplayName(driver) : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2 text-xs text-gray-600">
                        {driver.functie || '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2 text-xs text-gray-600">
                        {driver.cnp || '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 capitalize">
                          {driver.role === 'fleet_manager' ? 'Manager Flotă' : 
                           driver.role === 'tesa' ? 'TESA' : 
                           driver.role === 'sofer_cursa_lunga' ? 'Șofer Cursă Lungă' : 
                           driver.role === 'driver' ? 'Driver' : 
                           driver.role}
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                          {driver.norma_ore || 8}h
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        <div className="text-[10px] text-slate-600 leading-tight">
                          <div className="font-medium">
                            {driver.program_start || '08:00'}-{driver.program_sfarsit || '17:00'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                              driver.pontaj_pana_la 
                                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                                : driver.in_pontaj !== false 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}>
                              {driver.pontaj_pana_la ? (
                                <>
                                  <CalendarX className="w-3 h-3" />
                                  Până la {new Date(driver.pontaj_pana_la).toLocaleDateString('ro-RO', { month: 'short', year: 'numeric', timeZone: 'UTC' })}
                                </>
                              ) : driver.in_pontaj !== false ? (
                                <>
                                  <UserCheck className="w-3 h-3" />
                                  Activ
                                </>
                              ) : (
                                <>
                                  <UserX className="w-3 h-3" />
                                  Exclus
                                </>
                              )}
                              <ChevronDown className="w-3 h-3 ml-0.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuItem
                              onClick={async () => {
                                try {
                                  if (driver.pontaj_pana_la) {
                                    const endDate = new Date(driver.pontaj_pana_la);
                                    const now = new Date();
                                    const resignationExpired = now.getFullYear() > endDate.getUTCFullYear() || 
                                      (now.getFullYear() === endDate.getUTCFullYear() && now.getMonth() > endDate.getUTCMonth());
                                    
                                    if (resignationExpired) {
                                      const confirmed = confirm(
                                        `⚠️ ATENȚIE!\n\n` +
                                        `Angajatul "${driver.name || driver.username}" are raporturile de muncă încetate din ${endDate.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric', timeZone: 'UTC' })}.\n\n` +
                                        `Reactivarea în pontaj poate duce la erori în rapoarte și calcule.\n\n` +
                                        `Sunteți ABSOLUT sigur că doriți să reactivați acest angajat?`
                                      );
                                      if (!confirmed) return;
                                    }
                                    
                                    await axios.patch(`${API}/users/${driver.id}/pontaj`, { in_pontaj: true }, getAuthHeaders());
                                    fetchDrivers();
                                    toast.success('Angajat activ în pontaj (demisia rămâne setată)');
                                  } else {
                                    await axios.patch(`${API}/users/${driver.id}/pontaj`, { in_pontaj: true, pontaj_pana_la: null }, getAuthHeaders());
                                    fetchDrivers();
                                    toast.success('Angajat activ în pontaj');
                                  }
                                } catch (e) { toast.error('Eroare la actualizare'); }
                              }}
                              className="cursor-pointer"
                            >
                              <UserCheck className="w-4 h-4 mr-2 text-green-600" />
                              <span>Activ în pontaj</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={async () => {
                                try {
                                  await axios.patch(`${API}/users/${driver.id}/pontaj`, { in_pontaj: false }, getAuthHeaders());
                                  fetchDrivers();
                                  toast.success(driver.pontaj_pana_la 
                                    ? 'Angajat exclus din pontaj (demisia rămâne setată)' 
                                    : 'Angajat exclus din pontaj');
                                } catch (e) { toast.error('Eroare la actualizare'); }
                              }}
                              className="cursor-pointer"
                            >
                              <UserX className="w-4 h-4 mr-2 text-slate-500" />
                              <span>Exclus din pontaj</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setPontajEndDateDialog({
                                  open: true,
                                  userId: driver.id,
                                  userName: driver.username,
                                  currentDate: driver.pontaj_pana_la ? new Date(driver.pontaj_pana_la) : null
                                });
                              }}
                              className="cursor-pointer"
                            >
                              <CalendarX className="w-4 h-4 mr-2 text-orange-600" />
                              <span>Setează ultima lună...</span>
                            </DropdownMenuItem>
                            {driver.pontaj_pana_la && (
                              <>
                              <DropdownMenuItem
                                onClick={async () => {
                                  if (confirm('Sigur doriți să anulați demisia? Angajatul va deveni complet activ și toate marcajele X vor fi șterse.')) {
                                    try {
                                      await axios.post(
                                        `${API}/pontaj/anuleaza-demisie-complet?employee_id=${driver.id}`,
                                        {},
                                        getAuthHeaders()
                                      );
                                      await fetchPontajData();
                                      fetchDrivers();
                                      toast.success('Demisia a fost anulată complet. Toate marcajele X au fost șterse.');
                                    } catch (e) { toast.error('Eroare la anularea demisiei'); }
                                  }
                                }}
                                className="cursor-pointer text-red-600"
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                <span>Anulează demisia</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setDataReangajare(new Date().toISOString().split('T')[0]);
                                  setReactivareDialog({ open: true, driver });
                                }}
                                className="cursor-pointer text-blue-600"
                                data-testid={`reactivare-btn-${driver.id}`}
                              >
                                <UserCheck className="w-4 h-4 mr-2" />
                                <span>Reactivează (reangajare)</span>
                              </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        {driver.id_expiry ? (
                          <span className={`text-[10px] ${new Date(driver.id_expiry) < new Date() ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                            {new Date(driver.id_expiry).toLocaleDateString('ro-RO', {day: '2-digit', month: '2-digit', year: '2-digit'})}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        {driver.driver_licence_expiry ? (
                          <span className={`text-[10px] ${new Date(driver.driver_licence_expiry) < new Date() ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                            {new Date(driver.driver_licence_expiry).toLocaleDateString('ro-RO', {day: '2-digit', month: '2-digit', year: '2-digit'})}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2">
                        {driver.work_medicine_expiry ? (
                          <span className={`text-[10px] ${new Date(driver.work_medicine_expiry) < new Date() ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                            {new Date(driver.work_medicine_expiry).toLocaleDateString('ro-RO', {day: '2-digit', month: '2-digit', year: '2-digit'})}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-2 font-medium uppercase text-[10px]">{driver.catg || '-'}</TableCell>
                      <TableCell className="py-1.5 px-2 text-[10px] text-slate-600">{driver.created_by || '-'}</TableCell>
                      <TableCell className="py-1.5 px-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {hasPermission('angajati.editare') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleEditDriver(driver)}
                              data-testid={`edit-driver-${driver.id}`}
                            >
                              <Edit className="w-3.5 h-3.5 text-blue-500" />
                            </Button>
                          )}
                          {hasPermission('angajati.editare') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleToggleAcces(driver.id, driver.acces_blocat)}
                              data-testid={`toggle-acces-${driver.id}`}
                              title={driver.acces_blocat ? "Deblochează Acces" : "Blochează Acces"}
                            >
                              {driver.acces_blocat ? (
                                <Lock className="w-3.5 h-3.5 text-red-500" />
                              ) : (
                                <LockOpen className="w-3.5 h-3.5 text-green-500" />
                              )}
                            </Button>
                          )}
                          {hasPermission('angajati.editare') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleOpenResetPassword(driver)}
                              data-testid={`reset-password-${driver.id}`}
                              title="Resetare Parolă"
                            >
                              <Key className="w-3.5 h-3.5 text-amber-500" />
                            </Button>
                          )}
                          {hasPermission('angajati.stergere') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleDeleteDriver(driver.id)}
                              data-testid={`delete-driver-${driver.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Reactivare Angajat */}
      <Dialog open={reactivareDialog.open} onOpenChange={(open) => { if (!open) setReactivareDialog({ open: false, driver: null }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reactivare angajat</DialogTitle>
            <DialogDescription>
              {reactivareDialog.driver && (
                <>Reactivează angajatul <strong>{reactivareDialog.driver.name || reactivareDialog.driver.username}</strong>. Istoricul (pontaj, salarii, popriri) va fi păstrat.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="data-reangajare">Data reangajării</Label>
              <Input
                id="data-reangajare"
                type="date"
                value={dataReangajare}
                onChange={(e) => setDataReangajare(e.target.value)}
                data-testid="data-reangajare-input"
              />
            </div>
            {reactivareDialog.driver?.pontaj_pana_la && (
              <p className="text-xs text-slate-500">
                Demisia anterioară: {new Date(reactivareDialog.driver.pontaj_pana_la).toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' })}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setReactivareDialog({ open: false, driver: null })}>
              Renunță
            </Button>
            <Button onClick={handleReactivare} disabled={!dataReangajare} data-testid="confirm-reactivare-btn">
              Reactivează
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default AngajatiTab;
